import { PIN_LENGTH } from '@veriverse/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInteract } from '../net/interact.js';
import { pressPinKey, usePinDoorStore } from '../state/pin-door.js';
import { useSessionStore } from '../state/session-store.js';

/**
 * The PIN keypad of a PIN door (ADR-0039). Digits come from the keyboard or the buttons; the
 * fourth digit sends the PIN to the server, which answers with `unlocked`, `wrong` or `locked`.
 * Escape, or walking away from the door, closes it.
 */
export function PinDoorPanel({ objectId }: { objectId: number }) {
  const { t } = useTranslation();
  const feedback = usePinDoorStore((s) => s.feedback);
  const targetId = useSessionStore((s) => s.interactionTarget?.objectId);
  const [entered, setEntered] = useState('');
  const locked = feedback?.kind === 'locked' && feedback.retryAt > Date.now();
  const close = () => usePinDoorStore.getState().close();

  useEffect(() => {
    if (targetId !== objectId) usePinDoorStore.getState().close();
  }, [targetId, objectId]);

  // A wrong PIN clears the digits so the next try starts fresh.
  useEffect(() => {
    if (feedback) setEntered('');
  }, [feedback]);

  const press = (key: string) => {
    if (locked) return;
    const next = pressPinKey(entered, key);
    setEntered(next);
    if (next.length === PIN_LENGTH && next !== entered) {
      sendInteract(objectId, 'enter', { pin: next });
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        usePinDoorStore.getState().close();
        return;
      }
      if (event.key === 'Backspace' || /^\d$/.test(event.key)) press(event.key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="hud-box pin-door" role="dialog" aria-label={t('pinDoor.title')}>
      <strong>{t('pinDoor.title')}</strong>
      <div className="pin-door-display" aria-live="polite">
        {Array.from({ length: PIN_LENGTH }, (_, i) => (
          <span key={i}>{i < entered.length ? '•' : '–'}</span>
        ))}
      </div>
      <div className="pin-door-message" role="status">
        {feedback?.kind === 'wrong' && t('pinDoor.wrong', { count: feedback.attemptsLeft })}
        {locked && t('pinDoor.locked')}
      </div>
      <div className="pin-door-keys">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <button key={digit} type="button" className="secondary" onClick={() => press(digit)}>
            {digit}
          </button>
        ))}
        <button type="button" className="secondary" onClick={close}>
          {t('pinDoor.close')}
        </button>
        <button type="button" className="secondary" onClick={() => press('0')}>
          0
        </button>
        <button
          type="button"
          className="secondary"
          aria-label={t('pinDoor.erase')}
          onClick={() => press('Backspace')}
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
